'use client';

import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { LOCATION } from 'src/hooks/filters/constants';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import useDiscoveryList from 'src/hooks/discovery/useDiscoveryList';
import useIsAdvancedSearchActive from 'src/hooks/discovery/useIsAdvancedSearchActive';
import useDiscoveryEntityTypeLabel from 'src/hooks/discovery/useDiscoveryEntityTypeLabel';
import { useGetDiscoveryListQuery } from 'src/api/redux';
import {
  useExecuteAdvancedSearchQuery,
  useGetAdvancedSearchMetricsQuery,
} from 'src/api/redux/advancedSearches';
import { useDiscoveryListQueryParams } from '../../hooks/useDiscoveryQueryParams';
import { useAdvancedSearchQueryParams } from '../../hooks/useAdvancedSearchQueryParams';
import { Tabs, Checkbox, Search, Typography } from 'src/components';
import { useLogging } from 'src/context/logging';
import extractClaimsPatients from 'src/components/Discovery/utils/extractClaimsPatients';
import { ResultsContent, ResultSortDropdown } from '..';
import { ResultsFilterButton } from '../ResultsFilterButton';
import { useResultsList } from '../../context/ResultsListProvider';

const getErrorMessage = (error: unknown): string => {
  const errorData = error as { data?: { error_message?: string } };
  return (
    errorData?.data?.error_message ||
    'An error occurred while loading the results. Please try again.'
  );
};

const ResultsList = (): React.ReactElement => {
  const log = useLogging();

  const { setResultsSearch, q } = useDiscoveryList();
  const entityTypeLabel = useDiscoveryEntityTypeLabel('plural');
  const initialMountRef = useRef(true);

  // Reset search when component mounts or when returning to the page
  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false;
      if (q) {
        setResultsSearch('');
      }
    }
  }, [setResultsSearch]);

  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);
  const { apiFilterFormat } = useFilterSets(LOCATION.DISCOVERY);
  const { state, dispatch } = useResultsList();
  const isAdvancedSearchSelected = useIsAdvancedSearchActive();

  // Standard discovery list query — skip when advanced search is selected
  const { queryParams, skip } = useDiscoveryListQueryParams();
  const {
    data: queryData,
    isLoading: discoveryLoading,
    isFetching,
    isError: isDiscoveryError,
    error: discoveryError,
  } = useGetDiscoveryListQuery(queryParams, {
    skip: skip || isAdvancedSearchSelected,
  });

  // Advanced search execution query
  const { queryParams: advSearchParams, skip: skipAdvSearch } =
    useAdvancedSearchQueryParams();
  const {
    data: advSearchData,
    isLoading: advSearchLoading,
    isFetching: advSearchFetching,
    isError: isAdvSearchError,
    error: advSearchError,
  } = useExecuteAdvancedSearchQuery(advSearchParams, {
    skip: skipAdvSearch || !isAdvancedSearchSelected,
  });

  // Fetch metric headers for advanced search
  const { data: advSearchMetrics } = useGetAdvancedSearchMetricsQuery(
    advSearchParams?.id,
    { skip: !isAdvancedSearchSelected || !advSearchParams?.id }
  );

  // Merge into common shape
  const discoveryResults = useMemo(() => {
    if (isAdvancedSearchSelected && advSearchData) {
      const metricHeaders = advSearchMetrics?.results || [];
      return {
        results: advSearchData.results.map((provider) => {
          const { claims, patients } = extractClaimsPatients(
            provider.metrics,
            metricHeaders
          );
          return {
            id: provider.provider_id,
            provider_id: provider.provider_id,
            name: provider.name,
            type: provider.type,
            content_type: provider.type,
            specialty: provider.specialty,
            primary_address: {
              city: provider.city,
              state: provider.state,
            },
            address_line_1: provider.address_line_1,
            city: provider.city,
            state: provider.state,
            zip_code: provider.zip_code,
            lat: provider.lat,
            lng: provider.lng,
            claims,
            patients,
          };
        }),
        total_providers: advSearchData.count,
      };
    }
    if (skip || isAdvancedSearchSelected) {
      return { results: [], total_providers: 0 };
    }
    return queryData;
  }, [isAdvancedSearchSelected, advSearchData, advSearchMetrics, skip, queryData]);

  const isError = isAdvancedSearchSelected ? isAdvSearchError : isDiscoveryError;
  const error = isAdvancedSearchSelected ? advSearchError : discoveryError;

  const { selectedProviders } = state;
  const [currentPageSelected, setCurrentPageSelected] =
    useState<boolean>(false);

  // Combined loading state for initial load and revalidation
  const isLoadingData = isAdvancedSearchSelected
    ? advSearchLoading || advSearchFetching
    : discoveryLoading || isFetching;

  useEffect(() => {
    if (discoveryResults?.results?.length === 0) {
      setCurrentPageSelected(false);
      return;
    }
    const allCurrentPageSelected = discoveryResults?.results?.every((result) =>
      selectedProviders.some((p) => p.provider_id === result.provider_id)
    );
    setCurrentPageSelected(allCurrentPageSelected || false);
  }, [discoveryResults?.results, selectedProviders]);

  const handleSearch = (value: string) => {
    setResultsSearch(value);
  };

  const handleSelectAllResults = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const isChecked = e.target.checked;
      setCurrentPageSelected(isChecked);

      if (isChecked) {
        log.event('searchMultiSelectModeActivated');
        // Add current page results to existing selections, avoiding duplicates
        const newSelections = [...selectedProviders];
        discoveryResults?.results?.forEach((result) => {
          if (
            !selectedProviders.some((p) => p.provider_id === result.provider_id)
          ) {
            newSelections.push(result);
          }
        });
        dispatch({
          type: 'SET_SELECTED_PROVIDERS',
          payload: newSelections,
        });
      } else {
        // Only remove the current page items from selections
        dispatch({
          type: 'SET_SELECTED_PROVIDERS',
          payload: selectedProviders.filter(
            (provider) =>
              !discoveryResults?.results?.some(
                (result) => result.provider_id === provider.provider_id
              )
          ),
        });
      }
    },
    [discoveryResults?.results, dispatch, log, selectedProviders]
  );

  const count = discoveryResults?.total_providers;
  const results = discoveryResults?.results || [];

  const savedVolumeIsInProgress = appliedSavedVolume?.status === 'PROCESSING';
  const emptyParams =
    !apiFilterFormat && !apiFilterFormat?.length && !appliedSavedVolume;

  // Always set isCollapsed to false (never collapse)
  const isCollapsed = false;

  return (
    <div className="flex-1 min-h-0" data-testid="results-list-container">
      <Tabs className="h-full flex flex-col" aria-label="Discovery results">
        <div
          className={`align-stretch flex flex-col gap-2  items-start bg-white
            ${isCollapsed ? 'pt-8 pb-6 px-4' : 'p-4'}
          `}
        >
          <div className="w-full flex justify-between items-center">
            <div
              className="flex gap-1 text-primary text-base font-bold leading-normal"
              data-testid="results-count-display"
              aria-live="polite"
            >
              <span>
                {!isNaN(Number(count))
                  ? Number(count).toLocaleString('en-US')
                  : '0'}
              </span>
              <span>{entityTypeLabel}</span>
            </div>
          </div>
          <div
            className={`w-full transition-all duration-300 ease-out ${
              isCollapsed
                ? 'max-h-0 overflow-hidden opacity-0 -my-2'
                : 'max-h-none opacity-100'
            }`}
          >
            <div className="flex items-center gap-2">
              <ResultsFilterButton />
              <Search
                onSearch={handleSearch}
                placeholder="Search"
                size="sm"
                className="flex-1 rounded-full bg-white !h-9 !min-h-9 text-sm text-neutral-600"
                slotProps={{
                  iconClassName: '!text-neutral-600 !w-4 !h-4',
                }}
                aria-label="Search results"
                data-testid="results-search-input"
              />
            </div>
          </div>
        </div>

        <div
          className="self-stretch h-10 p-4 bg-neutral-50 border-t border-b border-neutral-300 inline-flex justify-between items-center"
          data-testid="results-selection-bar"
        >
          <div className="flex justify-start items-center gap-2.5">
            <Checkbox
              size="sm"
              color="primary"
              disabled={savedVolumeIsInProgress || emptyParams}
              checked={currentPageSelected || false}
              onChange={handleSelectAllResults}
              aria-label="Select all results on current page"
              data-testid="discovery-select-all-results-checkbox"
            />
            <label
              className="text-center justify-start text-secondary text-sm font-normal font-inter leading-tight"
              id="select-page-label"
            >
              Select Page
            </label>
          </div>
          {selectedProviders.length > 0 && (
            <div
              className="text-secondary text-xs font-semibold"
              aria-live="polite"
              data-testid="selected-providers-count"
            >
              {selectedProviders.length} Selected
            </div>
          )}
          <ResultSortDropdown />
        </div>
        {savedVolumeIsInProgress ? (
          <div
            className="flex flex-col items-center justify-center h-full p-8 border-t border-neutral-300"
            role="status"
            aria-live="polite"
            data-testid="encounter-in-progress-message"
          >
            <Typography
              align="center"
              color="secondary"
              className="pb-2 font-bold text-base text-center w-full"
            >
              Encounter In Progress
            </Typography>
            <Typography
              align="center"
              color="secondary"
              className="text-base text-center mx-auto max-w-md leading-6"
            >
              Your {appliedSavedVolume?.label || 'encounter'} volume is still
              being constructed. You will receive an email when it&apos;s
              complete and ready for use.
            </Typography>
          </div>
        ) : isError ? (
          <div
            className="flex flex-col items-center justify-center h-full p-8 border-t border-neutral-300"
            role="alert"
            aria-live="assertive"
            data-testid="results-error-message"
          >
            <Typography
              align="center"
              color="secondary"
              className="pb-2 font-bold text-base text-center w-full"
            >
              Error Loading Results
            </Typography>
            <Typography
              align="center"
              color="secondary"
              className="text-base text-center mx-auto max-w-md leading-6"
            >
              {getErrorMessage(error)}
            </Typography>
          </div>
        ) : (
          <div
            className="bg-white flex-1 overflow-y-auto"
            data-testid="results-content-container"
          >
            <div className="flex flex-col">
              <ResultsContent isLoading={isLoadingData} results={results} />
            </div>
          </div>
        )}
      </Tabs>
    </div>
  );
};

export default ResultsList;
